<template>
    <div class="space-y-2">
        <form-select name="3D object" :model-value="targetValue" @update:model-value="setTarget">
            <option value="">Select 3D object…</option>
            <option v-if="missingTarget" :value="targetValue">{{ targetValue }} — unavailable</option>
            <option v-for="target in model3dTargets" :key="target.value" :value="target.value">{{ target.label }}</option>
        </form-select>
        <p v-if="!model3dTargets.length" class="text-xs text-gray-300">No 3D objects on this page</p>

        <template v-if="selectedTarget">
            <p v-if="!resourceId" class="text-xs text-gray-300">This 3D object has no model assigned</p>
            <template v-else>
                <form-select name="Light" :model-value="action.light ?? ''" @update:model-value="setLight">
                    <option value="">Select light…</option>
                    <option v-if="missingLight" :value="action.light">Missing light: {{ action.light }}</option>
                    <option v-for="light in lights" :key="light.id" :value="light.id">
                        {{ formatH3dLightLabel(light) }}
                    </option>
                </form-select>
                <p v-if="lightsLoaded && !lights.length" class="text-xs text-gray-300">
                    No lights found in this 3D model
                </p>
                <p v-else-if="missingLight" class="text-xs text-red-400">
                    This light is no longer part of the 3D model
                </p>
                <form-select name="Property" :model-value="action.property ?? ''" @update:model-value="setProperty">
                    <option v-for="property in properties" :key="property.id" :value="property.id">
                        {{ property.label }}
                    </option>
                </form-select>
                <ActionPropertyValueEditor v-if="selectedProperty" :model-value="action.value"
                    :property-definition="selectedProperty" :context-variables="contextVariables"
                    @update:model-value="setValue" />
            </template>
        </template>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useModel3dStore } from '../../../stores/model3dStore.js';
import { formatH3dLightLabel } from '../../../3d/h3dLights.js';
import { H3D_LIGHT_PROPERTIES, getH3dLightProperty } from '../../../actions/model3dLightWireFormat.js';
import { variablesAcceptedByProperty } from '../../../actions/actionContextResolver.js';
import ActionPropertyValueEditor from './ActionPropertyValueEditor.vue';

const props = defineProps({
    node: { type: Object, required: true }, handler: { type: Object, required: true },
    action: { type: Object, required: true }, targets: { type: Array, required: true },
});
const emit = defineEmits(['change']);
const store = useModel3dStore();

const model3dTargets = computed(() => props.targets.filter(target => target.node?.obj === '3d'));
const targetValue = computed(() => props.action.target
    ? `${props.action.target.pageId}:${props.action.target.objectId}`
    : '');
const selectedTarget = computed(() => model3dTargets.value.find(target => target.value === targetValue.value)?.node);
const missingTarget = computed(() => targetValue.value && !selectedTarget.value);
const resourceId = computed(() => selectedTarget.value?.resource ?? null);

const properties = H3D_LIGHT_PROPERTIES;
const selectedProperty = computed(() => getH3dLightProperty(props.action.property));
const contextVariables = computed(() =>
    variablesAcceptedByProperty(props.node, props.handler.event, selectedProperty.value));

// Lights come from the resource metadata, so the editor never needs a mounted preview scene.
const lights = ref([]);
const lightsLoaded = ref(false);
watch(resourceId, uuid => {
    lights.value = [];
    lightsLoaded.value = false;
    if (!uuid) return;
    const cached = store.getModelLights(uuid);
    if (cached) {
        lights.value = cached;
        lightsLoaded.value = true;
        return;
    }
    store.ensureModelLights(uuid).then(result => {
        if (resourceId.value !== uuid) return;
        lights.value = result;
        lightsLoaded.value = true;
    });
}, { immediate: true });

// A light removed from the GLB stays selected so the action reads as broken rather than
// silently pointing at a different lamp.
const missingLight = computed(() =>
    props.action.light && lightsLoaded.value && !lights.value.some(light => light.id === props.action.light));

function setTarget(value) {
    if (value === targetValue.value) return;
    const [pageId, objectId] = value.split(':').map(Number);
    props.action.target = value ? { pageId, objectId } : null;
    // Light ids are only stable within one model, so a different object starts over.
    props.action.light = '';
    emit('change');
}
function setLight(value) {
    props.action.light = value;
    emit('change');
}
function setProperty(id) {
    const property = getH3dLightProperty(id);
    props.action.property = property ? property.id : '';
    props.action.value = property?.default ?? '';
    emit('change');
}
function setValue(value) {
    props.action.value = value;
    emit('change');
}
</script>
